import React from 'react';
import ReactDOM from 'react-dom';
import QRCode from 'qrcode.react';
import { isNullOrEmpty, isNullOrUndefined, safeFloat, safeInt, safeString } from './Utils';

export const INVOICE_PAYMENT_QR_MODE_DYNAMIC = 'dynamic';
export const INVOICE_PAYMENT_QR_MODE_STATIC = 'static';

export function getInvoicePaymentQRCodeMode(settings) {
    const qrMode = safeString(settings?.invoicePaymentQRCodeMode, INVOICE_PAYMENT_QR_MODE_DYNAMIC);
    if (qrMode === INVOICE_PAYMENT_QR_MODE_STATIC) {
        return INVOICE_PAYMENT_QR_MODE_STATIC;
    }
    return INVOICE_PAYMENT_QR_MODE_DYNAMIC;
}

export function buildUPIPaymentQRCodeValue(bankDetails, options = {}) {
    const upiId = safeString(bankDetails?.upiId);
    if (isNullOrEmpty(upiId)) {
        return '';
    }

    const params = [
        `pa=${encodeURIComponent(upiId)}`,
    ];

    const payeeName = safeString(bankDetails?.accountName || bankDetails?.bankName);
    if (!isNullOrEmpty(payeeName)) {
        params.push(`pn=${encodeURIComponent(payeeName)}`);
    }

    const note = safeString(options?.note);
    if (!isNullOrEmpty(note)) {
        params.push(`tn=${encodeURIComponent(note)}`);
    }

    const includeAmount = options?.includeAmount === true;
    const amount = options?.amount;
    if (includeAmount && !isNullOrUndefined(amount) && `${amount}` !== '') {
        const decimalPlaces = safeInt(options?.decimalPlaces, 2);
        const formattedAmount = safeFloat(amount).toFixed(decimalPlaces);
        params.push(`am=${encodeURIComponent(formattedAmount)}`);
        params.push(`cu=${encodeURIComponent(safeString(options?.currencyCode, 'INR'))}`);
    }

    return `upi://pay?${params.join('&')}`;
}

export function buildInvoicePaymentQRCodeValue(printData) {
    const voucherData = printData?.voucherData;
    const heading = safeString(printData?.heading, 'Invoice');
    const orderNumber = safeString(voucherData?.orderNumber);
    const note = isNullOrEmpty(orderNumber) ? heading : `${heading} ${orderNumber}`;

    return buildUPIPaymentQRCodeValue(printData?.bankDetails, {
        includeAmount: true,
        amount: voucherData?.grandTotal,
        decimalPlaces: safeInt(printData?.settings?.decimalPlaces, 2),
        currencyCode: safeString(printData?.subscriber?.currencyCode || printData?.settings?.currencyCode, 'INR'),
        note,
    });
}

export function buildStaticBankPaymentQRCodeValue(bankDetails) {
    return buildUPIPaymentQRCodeValue(bankDetails, { includeAmount: false });
}

export function generateQRCodeDataUrl(value, size = 200) {
    if (isNullOrEmpty(value) || typeof document === 'undefined') {
        return Promise.resolve('');
    }

    return new Promise((resolve) => {
        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.left = '-10000px';
        container.style.top = '-10000px';
        container.style.pointerEvents = 'none';
        document.body.appendChild(container);

        const cleanup = () => {
            try {
                ReactDOM.unmountComponentAtNode(container);
            } catch (error) {
                console.log(error);
            }
            if (container.parentNode) {
                container.parentNode.removeChild(container);
            }
        };

        const finalize = () => {
            try {
                const canvas = container.querySelector('canvas');
                resolve(canvas ? canvas.toDataURL('image/png') : '');
            } catch (error) {
                console.log(error);
                resolve('');
            } finally {
                cleanup();
            }
        };

        ReactDOM.render(
            React.createElement(QRCode, {
                value,
                size,
                includeMargin: true,
            }),
            container,
            () => {
                if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                    window.requestAnimationFrame(finalize);
                } else {
                    setTimeout(finalize, 0);
                }
            }
        );
    });
}

export async function prepareInvoicePrintDataWithQRCode(printData) {
    if (isNullOrUndefined(printData?.bankDetails)) {
        return printData;
    }

    const bankDetails = { ...printData.bankDetails };
    const existingQRCode = safeString(bankDetails.qrCode);
    const qrMode = getInvoicePaymentQRCodeMode(printData?.settings);

    let qrValue = '';
    if (qrMode === INVOICE_PAYMENT_QR_MODE_STATIC) {
        qrValue = buildStaticBankPaymentQRCodeValue(bankDetails);
    } else {
        qrValue = buildInvoicePaymentQRCodeValue(printData);
    }

    const generatedQRCode = await generateQRCodeDataUrl(qrValue, 200);
    if (qrMode === INVOICE_PAYMENT_QR_MODE_STATIC) {
        bankDetails.qrCode = safeString(existingQRCode || generatedQRCode);
    } else {
        bankDetails.qrCode = safeString(generatedQRCode || existingQRCode);
    }

    return {
        ...printData,
        bankDetails,
    };
}
